import { createFileRoute, Link } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { useAuth } from '@/lib/auth'
import { BRAND_MINT } from '@/lib/utils'
import {
  CopyIcon,
  CheckIcon,
  ExternalLinkIcon,
  SmartphoneIcon,
  UsersIcon,
  BanknoteIcon,
  ClockIcon,
  ArrowRightIcon,
} from 'lucide-react'
import { getEmployeesApi, getPaymentCyclesDueApi, getPendingRequestsApi, getDashboardSummaryApi } from '@/lib/api'
import type { DashboardSummary } from '@/lib/api'
import { Button } from '@/components/ui/button'
import {
  OutstandingCard,
  OverdueAmountCard,
  AverageCircleCard,
  MaxCircleCard,
  CompletionRateCard,
  ParticipationCard,
  OpenCirclesCard,
  AvgParticipantsCard,
  NewRequestsCard,
  NearingCapacityCard,
  CapacityCard,
  OverrideCard,
} from './components'

/** Small signature lockup: mint slash + dot, used on section headings. */
function MintSlashDot() {
  return (
    <div className="flex items-center gap-[3px]" aria-hidden="true">
      <span
        className="inline-block w-[2px] h-3.5"
        style={{ backgroundColor: BRAND_MINT }}
      />
      <span
        className="inline-block rounded-full"
        style={{ width: '4px', height: '4px', backgroundColor: BRAND_MINT }}
      />
    </div>
  )
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-3 flex items-center gap-2.5">
      <MintSlashDot />
      <h2 className="text-[11px] font-semibold text-muted-foreground/60 uppercase tracking-[0.12em] select-none">
        {children}
      </h2>
    </div>
  )
}

export const Route = createFileRoute('/dashboard/')({
  component: () => {
    const { user, token } = useAuth()
    const isEmployee = user?.role === 'employee'
    const [copied, setCopied] = useState(false)
    const [employeesCount, setEmployeesCount] = useState<number | null>(null)
    const [pendingPayments, setPendingPayments] = useState<number | null>(null)
    const [pendingRequests, setPendingRequests] = useState<number | null>(null)
    const [summary, setSummary] = useState<DashboardSummary | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
      async function loadStats() {
        try {
          const empId = user?.orgId || user?.employerId
          if (!empId) {
            setLoading(false)
            return
          }
          const [empRes, payRes, reqRes, summaryRes] = await Promise.allSettled([
            getEmployeesApi(empId, 1, 1),
            getPaymentCyclesDueApi(1, 1),
            getPendingRequestsApi(empId),
            getDashboardSummaryApi(),
          ])
          if (empRes.status === 'fulfilled') setEmployeesCount(empRes.value.total)
          if (payRes.status === 'fulfilled') setPendingPayments(payRes.value.total)
          if (reqRes.status === 'fulfilled') setPendingRequests(reqRes.value.data?.length || 0)
          if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value)
        } catch {
          // intentional: load failures fall through; setLoading(false) below handles state
        }
        setLoading(false)
      }
      loadStats()
    }, [user?.employerId])

    const handleCopy = () => {
      if (!token) return
      navigator.clipboard.writeText(token)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }

    // First-run = fresh signup landing on a still-empty registry.
    // Returning users who manually deleted everyone also see this card;
    // that is correct — the single most useful next action is the same.
    const isFirstRun = !loading && employeesCount === 0

    return (
      <div className="flex flex-col gap-6">
        {!isFirstRun && (
          <div className="flex items-start gap-3">
            <div
              aria-hidden="true"
              className="mt-0.5 shrink-0"
              style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
            />
            <div className="flex flex-col gap-1">
              <h1 className="text-2xl font-semibold tracking-tight">
                Welcome back{user?.name ? `, ${user.name}` : ''}
              </h1>
              <p className="text-muted-foreground/70 text-sm">
                Your payroll at a glance — track Funding Circles, payment cycles, and employee participation.
              </p>
            </div>
          </div>
        )}

        {loading && (
          <div
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
            aria-hidden="true"
          >
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="flex flex-col gap-3 rounded-xl border border-border bg-card p-5"
              >
                <span className="h-3 w-24 rounded bg-muted animate-pulse" />
                <span className="h-7 w-14 rounded bg-muted animate-pulse" />
              </div>
            ))}
          </div>
        )}

        {!loading && !isFirstRun && (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <StatCard label="Total employees" value={employeesCount ?? 0} icon={UsersIcon} />
              <StatCard label="Payroll runs" value={pendingPayments ?? 0} icon={BanknoteIcon} />
              <StatCard label="Pending payments" value={pendingRequests ?? 0} icon={ClockIcon} />
            </div>

            {summary && (
              <>
                <section>
                  <SectionHeading>Financial Overview</SectionHeading>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <OutstandingCard summary={summary} />
                    <OverdueAmountCard summary={summary} />
                    <AverageCircleCard summary={summary} />
                    <MaxCircleCard summary={summary} />
                    <CompletionRateCard summary={summary} />
                  </div>
                </section>

                <section>
                  <SectionHeading>People &amp; Activity</SectionHeading>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <ParticipationCard summary={summary} />
                    <OpenCirclesCard summary={summary} />
                    <AvgParticipantsCard summary={summary} />
                    <NewRequestsCard summary={summary} />
                    <NearingCapacityCard summary={summary} />
                    <OverrideCard summary={summary} />
                    <CapacityCard summary={summary} />
                  </div>
                </section>
              </>
            )}
          </>
        )}

        {isFirstRun && (
          <section
            aria-labelledby="first-run-title"
            className="relative overflow-hidden rounded-xl border border-border bg-card py-7 pl-8 pr-7 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-4 motion-safe:duration-500"
          >
            {/* Brand mint anchor: a single 2px structural slash — distinct
                from /signup's slash+dot lockup so each surface speaks once. */}
            <span
              aria-hidden="true"
              className="absolute top-0 left-0 h-full w-[2px]"
              style={{ backgroundColor: BRAND_MINT }}
            />
            <h1
              id="first-run-title"
              className="font-['Source_Serif_4',ui-serif,Georgia,serif] text-3xl font-medium leading-tight tracking-tight"
            >
              Add your first employee to get started.
            </h1>
            <p className="mt-2 max-w-prose text-sm text-muted-foreground/80">
              Your registry is empty. Payroll starts with your people — name,
              country, payment plan. We'll walk you through the first cycle.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Button asChild size="default">
                <Link to="/dashboard/add-employee">
                  <UsersIcon />
                  Add your first employee
                  <ArrowRightIcon />
                </Link>
              </Button>
            </div>
          </section>
        )}

        {!isFirstRun && token && !import.meta.env.PROD && (
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold">Auth Token</span>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                {copied ? (
                  <>
                    <CheckIcon className="size-3.5 text-emerald-500" />
                    Copied
                  </>
                ) : (
                  <>
                    <CopyIcon className="size-3.5" />
                    Copy
                  </>
                )}
              </button>
            </div>
            <code className="block break-all rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground font-mono">
              {token}
            </code>
          </div>
        )}

        {isEmployee && (
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex size-9 items-center justify-center rounded-lg bg-emerald-500/10 shrink-0">
                <SmartphoneIcon className="size-5 text-emerald-600" />
              </div>
              <div>
                <span className="text-sm font-semibold">Download the App</span>
                <p className="text-xs text-muted-foreground/80">
                  Get the mobile app for instant access to your payroll on the go
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <a
                href="https://play.google.com/store"
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-1 items-center gap-2.5 rounded-lg border border-border/50 bg-muted/50 px-4 py-2.5 text-sm font-medium transition-all hover:bg-muted hover:border-foreground/20 hover:shadow-sm"
              >
                <svg className="size-5 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.523 11.364 14.3 8.14l.21-.21a.47.47 0 0 1 .667 0l4.29 4.29a.48.48 0 0 1 0 .667l-4.29 4.29a.48.48 0 0 1-.668 0l-.21-.21 3.224-3.224a.65.65 0 0 0 0-.919l-.21-.21Zm0 0"/><path d="M3.34 2.605a.638.638 0 0 0-.34.563v17.664c0 .244.139.458.34.563l9.794-9.395L3.34 2.605Zm0 0M12.93 12 3.34 21.395a.638.638 0 0 0 .689-.06l4.93-3.534 3.97-3.97L12.93 12Zm0 0M14.3 8.14l-1.37 1.375 1.37 1.375 1.375-1.375L14.3 8.14Zm0 0M13.89 14.27l-4.93 3.534-3.97 3.97a.48.48 0 0 1-.667 0 .48.48 0 0 1 0-.668L13.89 14.27Zm0 0\"/>
                </svg>
                <div className="flex flex-col leading-tight">
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Get it on</span>
                  <span className="text-sm font-semibold">Google Play</span>
                </div>
                <ExternalLinkIcon className="ml-auto size-3 text-muted-foreground" />
              </a>
              <a
                href="https://apps.apple.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-1 items-center gap-2.5 rounded-lg border border-border bg-muted/50 px-4 py-2.5 text-sm font-medium transition-all hover:bg-muted hover:border-foreground/20 hover:shadow-sm"
              >
                <svg className="size-5 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11Z"/>
                </svg>
                <div className="flex flex-col leading-tight">
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Download on the</span>
                  <span className="text-sm font-semibold">App Store</span>
                </div>
                <ExternalLinkIcon className="ml-auto size-3 text-muted-foreground" />
              </a>
            </div>
          </div>
        )}
      </div>
    )
  },
})

function StatCard({ label, value, icon: Icon }: { label: string; value: number; icon: React.ElementType }) {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-border bg-card p-5 transition-all hover:shadow-lg hover:-translate-y-0.5">
      <div className="flex items-center gap-2">
        <Icon className="size-5 shrink-0 text-[var(--brand-mint)]" />
        <span className="text-sm text-muted-foreground">{label}</span>
      </div>
      <span className="text-2xl font-semibold tabular-nums">{value}</span>
    </div>
  )
}
